import { useEffect, useState } from "react";
import { isStandalone } from "../../pwa/platform";
import { onInstallChange, wasInstalled } from "../../pwa/install";

const DISMISSED_KEY = "pointz.installBanner.dismissedAt";
const QUIET_MS = 14 * 24 * 60 * 60 * 1000;

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return at > 0 && Date.now() - at < QUIET_MS;
  } catch {
    return false;
  }
}

/**
 * A nudge on Home to put Pointz on the home screen, shown only in a browser tab. Dismissing it
 * keeps it away for 14 days on this device: long enough not to nag, short enough that a parent
 * who meant "later" sees it again.
 */
export function InstallBanner({ onOpen }: { onOpen: () => void }) {
  const [hidden, setHidden] = useState(() => isStandalone() || dismissedRecently());

  useEffect(
    () =>
      onInstallChange(() => {
        if (wasInstalled()) setHidden(true);
      }),
    [],
  );

  if (hidden) return null;

  function dismiss() {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* storage blocked: hidden until the next load */
    }
    setHidden(true);
  }

  return (
    <aside className="install-banner" aria-label="Install Pointz">
      <p className="install-banner__text">Put Pointz on your home screen</p>
      <div className="install-banner__actions">
        <button className="button button--small button--primary" type="button" onClick={onOpen}>
          Show me how
        </button>
        <button className="button button--small" type="button" onClick={dismiss}>
          Not now
        </button>
      </div>
    </aside>
  );
}
